import { z } from 'zod';

import { isDestinationReelUrl } from '../utils/destination-reel-url.js';

import { UuidSchema } from './common.js';

/**
 * WebsiteSection `dataJson` shapes (Phase 5 - CMS-driven homepage).
 *
 * Each WebsiteSection row stores a free-form JSON payload whose shape depends
 * on `sectionType`. We validate that payload at two boundaries:
 *
 *   1. API ingest (CMS admins editing a section) - reject malformed shapes
 *      before they reach the DB.
 *   2. Website render-time - narrow `unknown` to a typed shape so that
 *      components can safely consume the data without per-field guards.
 *
 * `parseSectionData(sectionType, raw)` dispatches to the right schema for
 * a given type. Component code should pass the row through it once and then
 * read the typed result.
 *
 * Limits (max counts, string lengths) match the design system and the DB
 * column sizes; they are intentionally generous but bounded so the editor
 * UI can show counters and so payloads stay diff-friendly.
 *
 * Field-naming convention: dataJson payloads use snake_case keys
 * (image_media_id, cta_text) rather than the camelCase used by sibling
 * schemas (leads, customers, bookings). Reason: these blobs are admin-
 * editable JSONB content stored verbatim in WebsiteSection.dataJson -
 * snake_case round-trips cleanly through admin form generation and stays
 * consistent across DB column inspection, JSON-API responses and the
 * editor UI without per-layer casing rewrites.
 */

// ---------------------------------------------------------------------------
// Reusable fragments
// ---------------------------------------------------------------------------

const ItemId = UuidSchema;
const ColourHex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Hex colour like #F75A2F');

/**
 * Admin repeater rows start life with every field as "" (see `blank()` in
 * section-forms). Treat a blank/whitespace string as "not set" so a
 * half-filled row still saves instead of failing uuid/url validation.
 */
const blankable = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? null : v), schema);

// ---------------------------------------------------------------------------
// Section data schemas (one per WebsiteSectionType enum value)
// ---------------------------------------------------------------------------

/** A stat shown in the hero's trust strip (e.g. "100K+" / "Happy Travelers"). */
export const HeroStatSchema = z.object({
  value: z.string().min(1).max(24),
  label: z.string().min(1).max(60),
  /** lucide icon name OR a known brand key: google | facebook | instagram. */
  icon: z.string().max(40).nullish(),
});
export type HeroStat = z.infer<typeof HeroStatSchema>;

/**
 * A meta chip under the hero headline on the destination SEO guides
 * (e.g. calendar + "Updated 2026", cloud + "Weather Guide", clock +
 * "12 Min Read"). Only the guide hero renders these; every other page type
 * ignores them.
 */
export const HeroMetaItemSchema = z.object({
  text: z.string().min(1).max(40),
  /** lucide icon name (e.g. calendar / cloud / clock). */
  icon: z.string().max(40).nullish(),
});
export type HeroMetaItem = z.infer<typeof HeroMetaItemSchema>;

/**
 * What the hero search panel opens on when a page has never been told
 * otherwise: Delhi to Mumbai, a stay in Delhi, tomorrow. Exported because the
 * website and the editor both need them for a row saved before the fields
 * existed - that row carries none of them, and must still open filled in.
 */
export const HERO_SEARCH_DEFAULTS = {
  from: 'DEL',
  to: 'BOM',
  fromLabel: 'Delhi',
  toLabel: 'Mumbai',
  /** TBO `CityId` for "New Delhi / Delhi". */
  hotelCityId: '130443',
  hotelCityName: 'New Delhi / Delhi',
  hotelCountryName: 'India',
  daysAhead: 1,
} as const;

/** Blank for "leave the field empty", otherwise a three-letter IATA code. */
const HeroIata = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^([A-Z]{3})?$/, 'Three-letter IATA code');

export const HeroSectionDataSchema = z.object({
  /** Uploaded background video (media id → served via the CDN). Renders as a
   * muted, looped, controls-less autoplay <video>. Takes precedence over
   * video_url when both are set. */
  video_media_id: ItemId.nullish(),
  /** External direct video file URL (mp4/webm). Fallback/override when no
   * uploaded video is set. NOT a YouTube link - embeds are not supported. */
  video_url: z.string().max(500).nullish(),
  /** Poster/fallback image (also used when no video is set). Optional now
   * that video can drive the background. */
  image_media_id: ItemId.nullish(),
  /** Hero height preset, as a share of the viewport:
   *   - "full"  → full screen (100vh)
   *   - "half"  → half screen (50vh)
   *   - "third" → one third (33vh)
   * Industry-standard "viewport height" presets; defaults to full. */
  height: z.enum(['full', 'half', 'third']).default('full'),
  /**
   * Landing-page mode: strip every navigation link out of the site header so
   * only the logo remains (no top utility bar, no primary nav, no mobile
   * search / hamburger). Campaign pages want the visitor on the page, not
   * browsing the rest of the site. Defaults to OFF - every existing page keeps
   * the full header untouched.
   */
  hide_site_nav: z.boolean().default(false),
  // Headline + subhead live on the section row (title / subtitle columns),
  // not in dataJson - the single source of truth for every section's heading.
  /** Inline search box ("What are you looking for?"). */
  search_enabled: z.boolean().default(false),
  search_placeholder: z.string().max(120).nullish(),
  search_action: z.string().max(200).nullish(),
  cta_text: z.string().max(40).nullish(),
  /**
   * Optional href kept for backwards compatibility. The hero CTA opens the
   * site enquiry popup on click; this link is no longer used for navigation.
   */
  cta_href: z.string().max(200).nullish(),
  /** Background colour for the hero CTA button. Defaults to brand yellow. */
  cta_colour: ColourHex.default('#FEE21C'),
  /** Show/hide the trust/stats strip at the bottom of the hero. */
  stats_enabled: z.boolean().default(true),
  /** Trust/stats strip content. */
  stats: z.array(HeroStatSchema).default([]),

  // ── Flight / hotel search widget ────────────────────────────────────────
  /**
   * Renders the search panel under the hero copy with the Flights tab enabled.
   * Opt-in, so every existing hero keeps its current layout. When either this
   * or `hotel_search_enabled` is on, the widget replaces the plain
   * `search_enabled` box - they never render together.
   *
   * `flight_search_enabled` and `hotel_search_enabled` are independent: turn on
   * one for a single-product panel (no tabs), or both for the Flights/Hotels
   * tab switcher.
   *
   * Show/hide is the ONLY thing the CMS controls here. Everything inside the
   * panel (trip type, cabin class, airports, dates, travellers, special fares)
   * is the visitor's to choose on the website, so none of it is stored as
   * section data. Airports and stay cities come live from the City master, so
   * the options track whatever ops maintain there with no redeploy.
   */
  flight_search_enabled: z.boolean().default(false),

  /**
   * Renders the search panel with the Hotels (stays) tab enabled. Independent
   * of `flight_search_enabled` - see that field's note.
   */
  hotel_search_enabled: z.boolean().default(false),

  /**
   * Adds an Activities tab to the search panel. There is no activities search
   * yet, so the tab opens a "coming soon" panel. Independent of the two flags
   * above - see `flight_search_enabled`.
   */
  activities_search_enabled: z.boolean().default(false),

  /**
   * Which tab the search panel opens on, when more than one is enabled.
   *
   * `auto` is every hero that existed before this field: Flights first, and a
   * visitor coming Back from results gets the tab they searched from. A named
   * tab means the PAGE owns the choice - /hotels opens on Hotels and
   * /activities on Activities however the visitor last searched; the restored
   * draft still refills the fields, it just does not move the tab. Ignored when
   * the named tab is not enabled.
   */
  search_default_tab: z.enum(['auto', 'flights', 'hotels', 'activities']).default('auto'),

  /**
   * On a page pinned to a tab (`search_default_tab` is not `auto`), make the
   * OTHER tabs links to that product's own landing page - Flights to /flights,
   * Hotels to /hotels, Activities to /activities - instead of only swapping the
   * form. On /hotels a visitor who picks Flights then gets the flight deals and
   * the flight headline too, not a flight form above a page of hotels.
   *
   * Off by default, and ignored while the tab is `auto`: the homepage toggle
   * must stay instant and must never carry a visitor off the homepage. It is a
   * switch rather than a rule so that it can be turned off from the CMS the day
   * a page change turns out to feel slower than the toggle it replaced.
   */
  search_tabs_navigate: z.boolean().default(false),

  /**
   * Open the panel already filled in, instead of on "Select a city".
   *
   * A blank form asks four questions before it shows a single fare; a filled
   * one is a search the visitor can run as it stands, or correct. This is the
   * one exception to "the CMS only shows and hides" above: what is stored is
   * the STARTING point, and every field is still the visitor's to change.
   *
   * The defaults live here rather than in each page's row, so every hero that
   * existed before these fields opens on them without anyone re-saving it.
   * Only a fresh visit is prefilled - a visitor coming Back gets their own
   * search handed back, never this.
   */
  search_prefill_enabled: z.boolean().default(true),
  /** IATA codes of the flight form's From / To. Blank leaves that field empty. */
  search_default_from: HeroIata.default(HERO_SEARCH_DEFAULTS.from),
  search_default_to: HeroIata.default(HERO_SEARCH_DEFAULTS.to),
  /** City names for the editor only - the website resolves the codes itself. */
  search_default_from_label: z.string().max(120).default(HERO_SEARCH_DEFAULTS.fromLabel),
  search_default_to_label: z.string().max(120).default(HERO_SEARCH_DEFAULTS.toLabel),
  /**
   * The stay form's destination: a TBO `CityId` plus the names the closed field
   * shows. All three travel together because a city id cannot be turned back
   * into a name without a lookup the panel would have to wait for.
   */
  search_default_hotel_city_id: z.string().trim().max(40).default(HERO_SEARCH_DEFAULTS.hotelCityId),
  search_default_hotel_city_name: z.string().max(160).default(HERO_SEARCH_DEFAULTS.hotelCityName),
  search_default_hotel_country_name: z
    .string()
    .max(120)
    .default(HERO_SEARCH_DEFAULTS.hotelCountryName),
  /**
   * How many days from the visitor's today the prefilled departure / check-in
   * falls. Relative on purpose: a stored date would be in the past a day later.
   * 1 is tomorrow; the stay checks out one night after.
   */
  search_default_days_ahead: z
    .number()
    .int()
    .min(0)
    .max(60)
    .default(HERO_SEARCH_DEFAULTS.daysAhead),

  /**
   * Meta chips under the headline, rendered only by the guide hero variant
   * (BEST_TIME_TO_VISIT). Left empty everywhere else.
   */
  meta_items: z.array(HeroMetaItemSchema).default([]),
});
export type HeroSectionData = z.infer<typeof HeroSectionDataSchema>;

export const ServiceIconsRowDataSchema = z.object({
  items: z
    .array(
      z.object({
        icon_media_id: ItemId.nullish(),
        label: z.string().max(40).default(''),
        href: z.string().max(200).default('/'),
      }),
    )
    .default([]),
});
export type ServiceIconsRowData = z.infer<typeof ServiceIconsRowDataSchema>;

/** One celebrity/star card in the "Stars Travelled With Us" slider. */
export const StarEndorsementSchema = z.object({
  name: z.string().max(80).default(''),
  /** Role / one-line description shown under the name. */
  blurb: z.string().max(280).default(''),
  photo_media_id: ItemId.nullish(),
  /** Card background colour. */
  bg_colour: ColourHex.default('#093F51'),
});
export type StarEndorsement = z.infer<typeof StarEndorsementSchema>;

export const StarEndorsementsDataSchema = z.object({
  // Heading + subhead come from the section's title / subtitle columns.
  items: z.array(StarEndorsementSchema).default([]),
  // ── Slider behaviour (all admin-configurable) ──
  autoplay: z.boolean().default(true),
  /** Autoplay delay between slides, ms. */
  autoplay_ms: z.number().int().min(1000).max(20000).default(5000),
  pause_on_hover: z.boolean().default(true),
  loop: z.boolean().default(true),
  show_arrows: z.boolean().default(true),
  show_dots: z.boolean().default(false),
  /** How many cards are visible at once, per breakpoint. */
  per_view_desktop: z.number().int().min(1).max(6).default(3),
  per_view_tablet: z.number().int().min(1).max(4).default(2),
  per_view_mobile: z.number().int().min(1).max(2).default(1),
  /** How many cards advance per scroll (manual + autoplay) at each breakpoint. */
  slides_to_scroll_desktop: z.number().int().min(1).max(5).default(1),
  slides_to_scroll_tablet: z.number().int().min(1).max(5).default(1),
  slides_to_scroll_mobile: z.number().int().min(1).max(5).default(1),
});
export type StarEndorsementsData = z.infer<typeof StarEndorsementsDataSchema>;

export const FeaturedCategoryDataSchema = z.object({
  category_label: z.string().max(60).default('Featured'),
  featured: z
    .object({
      destination_id: ItemId.nullish(),
      price_text: z.string().max(60).default(''),
      cta_text: z.string().max(40).default(''),
      cta_href: z.string().max(200).default('/'),
    })
    .nullish(),
  tiles: z
    .array(z.object({ destination_id: ItemId.nullish() }))
    .max(3)
    .default([]),
});
export type FeaturedCategoryData = z.infer<typeof FeaturedCategoryDataSchema>;

/**
 * "Editor's Picks" block. Cards REFERENCE published blog posts (by id) rather
 * than storing copied content - so the title / excerpt / cover image / date /
 * link are always resolved live from the blog (single source of truth), exactly
 * like FEATURED_CATEGORY references destinations. The website resolves the ids
 * via GET /website/blogs/batch at render time.
 *
 * Heading ("Editor's Picks") + subheading ("Featured for members") come from
 * the section's title / subtitle columns, like every other section.
 */
export const EditorsPicksDataSchema = z.object({
  /** The single large card on the left - one published blog post. */
  featured_blog_id: ItemId.nullish(),
  /** Up to 4 small cards on the right - published blog posts. Blank entries
   * (rows the editor added but hasn't picked yet) are dropped before validation
   * so an in-progress pick never fails the save. */
  pick_blog_ids: z
    .preprocess(
      (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x.length > 0) : v),
      z.array(ItemId).max(4),
    )
    .default([]),
});
export type EditorsPicksData = z.infer<typeof EditorsPicksDataSchema>;

/**
 * "Explore all stories" - a faceted blog/story filter section: a left filter
 * rail (Destination + Category/Interest, with Theme reserved for later) plus a
 * paginated story grid. Story content is resolved live from published blog
 * posts via GET /website/blogs/explore (single source of truth); the filter
 * options come from GET /website/blogs/filters. Selection lives in the URL
 * query (shareable + crawlable). Heading / subhead come from the section's
 * title / subtitle columns, like every other section.
 */
export const ExploreStoriesDataSchema = z.object({
  /** Which filter groups the rail shows. All three masters (destination,
   * category, theme) exist, so all default on. */
  show_destination_filter: z.boolean().default(true),
  show_interest_filter: z.boolean().default(true),
  show_theme_filter: z.boolean().default(true),
  /** Show the search box above the grid. */
  search_enabled: z.boolean().default(true),
  /** Optional pre-selected filter ids, applied only when the URL carries none. */
  default_destination_ids: z.array(ItemId).default([]),
  default_category_ids: z.array(ItemId).default([]),
  /** Cards per page / "Load more" batch. */
  page_size: z.number().int().min(3).max(48).default(12),
  /** Default ordering of the grid. */
  sort_order: z.enum(['newest', 'oldest']).default('newest'),
});
export type ExploreStoriesData = z.infer<typeof ExploreStoriesDataSchema>;

export const CtaBannerDataSchema = z.object({
  // Banner title comes from the section's title column.
  date_text: z.string().max(40).nullish(),
  price_text: z.string().max(60).nullish(),
  cta_href: z.string().max(200).default('/'),
  bg_colour: ColourHex.nullish(),
  /**
   * Button label. Only the destination guides' rail promo shows a labelled
   * button; the full-width banner has its own fixed CTA.
   */
  cta_text: z.string().max(40).nullish(),
});
export type CtaBannerData = z.infer<typeof CtaBannerDataSchema>;

export const FeatureListDataSchema = z.object({
  items: z
    .array(
      z.object({
        // Optional - the section renders 01/02/03 number badges, not icons.
        icon: z.string().max(40).default(''),
        title: z.string().max(80).default(''),
        body: z.string().max(280).default(''),
      }),
    )
    .default([]),
  illustration_media_id: ItemId.nullish(),
  photo_media_ids: z.array(ItemId).max(4).default([]),
  /**
   * Small label above the heading ("Packing guide"). Drawn only by the
   * destination guides' pill grid.
   */
  eyebrow: z.string().max(60).nullish(),
  /**
   * Layout variant:
   *   - `default` → centred title, left item list, right illustration + 2 photos.
   *   - `split`   → centred illustration with items split to either side (the
   *                 first half left, the rest right); the 2 photos are unused.
   */
  layout: z.enum(['default', 'split']).default('default'),
});
export type FeatureListData = z.infer<typeof FeatureListDataSchema>;

export const ClientStoriesDataSchema = z.object({
  items: z
    .array(
      z.object({
        photo_media_id: ItemId.nullish(),
        quote: z.string().max(2000).default(''),
        rating: z.number().int().min(1).max(5).default(5),
        traveller_name: z.string().max(80).default(''),
      }),
    )
    .default([]),
  /**
   * Compact spacing: drops the section's top padding so the cards sit flush
   * under the section above (e.g. the photo gallery).
   */
  compact: z.boolean().default(false),
  // ── Slider behaviour (all admin-configurable) ──
  autoplay: z.boolean().default(false),
  /** Autoplay delay between slides, ms. */
  autoplay_ms: z.number().int().min(1000).max(20000).default(5000),
  pause_on_hover: z.boolean().default(true),
  loop: z.boolean().default(false),
  show_arrows: z.boolean().default(true),
  show_dots: z.boolean().default(false),
  /** How many cards are visible at once, per breakpoint. */
  per_view_desktop: z.number().int().min(1).max(6).default(3),
  per_view_tablet: z.number().int().min(1).max(4).default(2),
  per_view_mobile: z.number().int().min(1).max(2).default(1),
  /** How many cards advance per scroll (manual + autoplay) at each breakpoint. */
  slides_to_scroll_desktop: z.number().int().min(1).max(5).default(1),
  slides_to_scroll_tablet: z.number().int().min(1).max(5).default(1),
  slides_to_scroll_mobile: z.number().int().min(1).max(5).default(1),
});
export type ClientStoriesData = z.infer<typeof ClientStoriesDataSchema>;

/**
 * REVIEWS - a general-purpose, reusable review carousel (distinct from
 * CLIENT_STORIES and not part of the MICE group). Each card: a gold star
 * rating, a quote, an optional "View details" link (per-review URL), and a
 * circular avatar beside the reviewer's name. Slider behaviour mirrors
 * CLIENT_STORIES so it can be dropped onto any page and tuned per breakpoint.
 */
export const ReviewsDataSchema = z.object({
  items: z
    .array(
      z.object({
        photo_media_id: ItemId.nullish(),
        quote: z.string().max(2000).default(''),
        rating: z.number().int().min(1).max(5).default(5),
        reviewer_name: z.string().max(80).default(''),
        /** "View details" target. Empty = no link shown. */
        href: z.string().max(300).default(''),
      }),
    )
    .default([]),
  /** Drop the section's top padding so it sits flush under the section above. */
  compact: z.boolean().default(false),
  /** Label for the per-review link. */
  link_label: z.string().max(40).default('View details'),
  // ── Slider behaviour (all admin-configurable) ──
  autoplay: z.boolean().default(false),
  autoplay_ms: z.number().int().min(1000).max(20000).default(5000),
  pause_on_hover: z.boolean().default(true),
  loop: z.boolean().default(false),
  show_arrows: z.boolean().default(true),
  show_dots: z.boolean().default(false),
  per_view_desktop: z.number().int().min(1).max(6).default(3),
  per_view_tablet: z.number().int().min(1).max(4).default(2),
  per_view_mobile: z.number().int().min(1).max(2).default(1),
  slides_to_scroll_desktop: z.number().int().min(1).max(5).default(1),
  slides_to_scroll_tablet: z.number().int().min(1).max(5).default(1),
  slides_to_scroll_mobile: z.number().int().min(1).max(5).default(1),
});
export type ReviewsData = z.infer<typeof ReviewsDataSchema>;

/**
 * AWARDS_BLOCK - the awards / credentials section, in two layouts:
 *
 *   `timeline` - "Our Award Winning Journey" horizontal timeline (homepage).
 *                Teal icon medallions alternate above / below a navy connector
 *                line. Icon-driven; reads best at 4-6 items.
 *   `cards`    - "Our Awards" stacked cards (the Our Awards page). Full-width
 *                rounded cards on an alternating blue / cyan gradient, each with
 *                a photo of the physical award on the left and title + blurb on
 *                the right. Falls back to the icon medallion when an item has no
 *                photo. Heading / supporting line come from the section's
 *                title / subtitle columns.
 *
 * Item photos resolve through the shared media batch (`items[].image_media_id`,
 * see PageSections' media collector) - no per-section wiring needed.
 */
export const AwardsBlockDataSchema = z.object({
  items: z
    .array(
      z.object({
        title: z.string().max(80).default(''),
        body: z.string().max(280).default(''),
        icon: z.string().max(40).default(''),
        /** Photo of the award itself. `cards` only; `timeline` ignores it. */
        image_media_id: ItemId.nullish(),
      }),
    )
    .default([]),
  variant: z.enum(['timeline', 'cards']).default('timeline'),
  cta_href: z.string().max(200).nullish(),
});
export type AwardsBlockData = z.infer<typeof AwardsBlockDataSchema>;

export const CallbackFormDataSchema = z.object({
  // Headline + subhead come from the section's title / subtitle columns.
  image_media_id: ItemId.nullish(),
  query_source_code: z.string().min(1).max(40).default('website_callback_form'),
});
export type CallbackFormData = z.infer<typeof CallbackFormDataSchema>;

/**
 * MICE_FORM - "Ready To Plan Your Next Mice Program?" corporate lead-capture
 * card. A pill-field grid (Company / Official email, then Name / Preferred
 * destination, then Number / Team size / Travel dates) over a centred "Let's
 * plan" button. The four MICE-specific fields (company, destination, team size,
 * travel dates) are folded into the lead `message` because the public form
 * endpoint only carries name / email / phone / message / sourceCode - so the
 * only configurable payload is the source code the lead is recorded under.
 * Heading + subhead come from the section's title / subtitle columns, like every
 * other section.
 */
export const MiceFormDataSchema = z.object({
  query_source_code: z.string().min(1).max(40).default('website-mice'),
});
export type MiceFormData = z.infer<typeof MiceFormDataSchema>;

export const FaqListDataSchema = z.object({
  // Heading comes from the section's title column (defaults to "Frequently
  // asked questions" on the website when blank). Each item is a Q&A pair.
  items: z
    .array(
      z.object({
        question: z.string().max(300).default(''),
        answer: z.string().max(2000).default(''),
      }),
    )
    .default([]),
});
export type FaqListData = z.infer<typeof FaqListDataSchema>;

export const PartnersLogosDataSchema = z.object({
  // Logos picked directly from the media library (no separate Partners catalog).
  // Headline + subhead come from the section's title / subtitle columns.
  media_ids: z.array(ItemId).default([]),
  // ── Slider behaviour (all admin-configurable) ──
  // Partners is rendered as TWO rows that scroll in opposite directions
  // (row 1 left→right, row 2 right→left); items are split across the rows.
  autoplay: z.boolean().default(true),
  /** Step delay (stepped mode) OR speed driver (continuous mode), ms. */
  autoplay_ms: z.number().int().min(1000).max(20000).default(4000),
  pause_on_hover: z.boolean().default(true),
  loop: z.boolean().default(true),
  /** Visible logos per row, per breakpoint. */
  per_view_desktop: z.number().int().min(2).max(8).default(4),
  per_view_tablet: z.number().int().min(2).max(6).default(3),
  per_view_mobile: z.number().int().min(1).max(4).default(2),
  /**
   * Continuous (marquee) mode. When ON the rows scroll smoothly without
   * stepped pauses; autoplay_ms drives the scroll speed (smaller = faster).
   * When OFF the rows step every autoplay_ms.
   */
  continuous_mode: z.boolean().default(false),
  /**
   * 2026-09-11. Show every logo in ONE row instead of splitting them across
   * the two counter-scrolling rows (e.g. "Our Destination Partnerships" with
   * five logos side by side). Opt-in - every existing section keeps its two
   * rows. In single-row mode with autoplay on, the strip repeats the logos so
   * a handful of them still scroll as a marquee.
   */
  single_row: z.boolean().default(false),
});
export type PartnersLogosData = z.infer<typeof PartnersLogosDataSchema>;

/**
 * MICE_ACCREDITATIONS - "Accreditations & Partners" logo grid for MICE pages
 * (e.g. tourism boards / accreditation marks). Logos are picked from the media
 * library and rendered as a STATIC grid (no slider/marquee). Headline + subhead
 * come from the section's title / subtitle columns.
 */
export const MiceAccreditationsDataSchema = z.object({
  /** Accreditation / partner logos, in display order. */
  media_ids: z.array(ItemId).default([]),
});
export type MiceAccreditationsData = z.infer<typeof MiceAccreditationsDataSchema>;

export const PressLogosDataSchema = z.object({
  // Press logos picked directly from the media library.
  // Headline + subhead come from the section's title / subtitle columns.
  media_ids: z.array(ItemId).default([]),
  // ── Slider behaviour (single row, scrolls left→right) ──
  autoplay: z.boolean().default(true),
  autoplay_ms: z.number().int().min(1000).max(20000).default(4000),
  pause_on_hover: z.boolean().default(true),
  loop: z.boolean().default(true),
  per_view_desktop: z.number().int().min(2).max(8).default(4),
  per_view_tablet: z.number().int().min(2).max(6).default(3),
  per_view_mobile: z.number().int().min(1).max(4).default(2),
  /** Continuous (marquee) mode - see PartnersLogosData for details. */
  continuous_mode: z.boolean().default(false),
});
export type PressLogosData = z.infer<typeof PressLogosDataSchema>;

/**
 * MICE_PARTNERS_MARQUEE - single-row scrolling marquee of partner / client
 * logos for MICE pages (the "trusted by" logo strip). Logos are picked directly
 * from the media library; headline + subhead come from the section's title /
 * subtitle columns. Same admin-configurable slider behaviour as PRESS_LOGOS.
 */
export const MicePartnersMarqueeDataSchema = z.object({
  media_ids: z.array(ItemId).default([]),
  // ── Slider behaviour (single row, scrolls left→right) ──
  autoplay: z.boolean().default(true),
  autoplay_ms: z.number().int().min(1000).max(20000).default(4000),
  pause_on_hover: z.boolean().default(true),
  loop: z.boolean().default(true),
  per_view_desktop: z.number().int().min(2).max(8).default(5),
  per_view_tablet: z.number().int().min(2).max(6).default(3),
  per_view_mobile: z.number().int().min(1).max(4).default(2),
  /** Continuous (marquee) mode - see PartnersLogosData for details. */
  continuous_mode: z.boolean().default(true),
});
export type MicePartnersMarqueeData = z.infer<typeof MicePartnersMarqueeDataSchema>;

/**
 * MICE_SERVICES - "Our MICE Services" four-pillar grid (e.g. Incentive Travel /
 * Conference Logistics / Team Offsites / Leadership Retreats). Each pillar is an
 * uploaded icon (shown white on a navy circle), a title, a short description,
 * and a bullet list of inclusions. Heading + subhead come from the section's
 * title / subtitle columns; `columns` (2-4) sets the desktop pillars-per-row.
 */
export const MiceServicePillarSchema = z.object({
  icon_media_id: ItemId.nullish(),
  title: z.string().max(80).default(''),
  description: z.string().max(400).default(''),
  /** Inclusion bullets shown under the description (one entry per bullet). */
  bullets: z.array(z.string().max(160)).default([]),
});
export type MiceServicePillar = z.infer<typeof MiceServicePillarSchema>;

export const MiceServicesDataSchema = z.object({
  items: z.array(MiceServicePillarSchema).default([]),
  /** Pillars per row on desktop. */
  columns: z.number().int().min(2).max(4).default(4),
});
export type MiceServicesData = z.infer<typeof MiceServicesDataSchema>;

/**
 * MICE_CASE_STUDIES - "Trusted By Leading Brands" success-story cards. Each item
 * is a full-width case-study card: a category / region eyebrow, a No.-of-people
 * + location stat pair, a title, an intro paragraph, a "What we delivered"
 * bullet list, one large hero image, and a row of supporting thumbnails. The
 * section heading + subhead come from the section's title / subtitle columns.
 * Cards stack vertically - add as many as you like.
 */
export const MiceCaseStudyItemSchema = z.object({
  /** Client logo shown top-left, before the category label (e.g. the MERU mark). */
  logo_media_id: ItemId.nullish(),
  /** Small eyebrow before the dot separator, e.g. "Technology". */
  category: z.string().max(80).default(''),
  /** Scope / region after the dot, e.g. "Pan-India". */
  region: z.string().max(80).default(''),
  /** Card title, e.g. "Pan-India Hotel Management for TCS iON". */
  title: z.string().max(200).default(''),
  /** Challenge / intro paragraph. */
  description: z.string().max(1500).default(''),
  /** "What we delivered" bullet list (one entry per bullet). */
  deliverables: z.array(z.string().max(240)).default([]),
  /** "No. of People" stat value, e.g. "100+ People". */
  people: z.string().max(60).default(''),
  /** "Location" stat value, e.g. "India". */
  location: z.string().max(80).default(''),
  /** Large hero image. */
  image_media_id: ItemId.nullish(),
  /** Supporting thumbnails shown beneath the copy. */
  gallery_media_ids: z.array(ItemId).default([]),
});
export type MiceCaseStudyItem = z.infer<typeof MiceCaseStudyItemSchema>;

export const MiceCaseStudiesDataSchema = z.object({
  items: z.array(MiceCaseStudyItemSchema).default([]),
});
export type MiceCaseStudiesData = z.infer<typeof MiceCaseStudiesDataSchema>;

/**
 * MICE_DESTINATIONS - "Destinations We Cover" grouped carousels. The section
 * heading + subhead come from the section's title / subtitle columns; below sit
 * one or more groups (e.g. "Domestic MICE" / "International MICE"), each with its
 * own heading, subheading, a small per-card eyebrow, and a horizontal carousel
 * of destination cards. Cards reference the Destination master by id, so the
 * name + image + /destinations/{slug} link come from there (swap the id list for
 * a MICE-specific source later without changing the UI).
 */
export const MiceDestinationGroupSchema = z.object({
  /** Group heading, e.g. "Domestic MICE". */
  heading: z.string().max(80).default(''),
  /** Group subheading, e.g. "Pan-India Coverages". */
  subheading: z.string().max(120).default(''),
  /** Small uppercase eyebrow shown on every card, e.g. "PAN-INDIA COVERAGES". */
  card_eyebrow: z.string().max(60).default(''),
  /** Rounded band colour behind the group header (hex). Empty = default tint. */
  accent_colour: z.string().max(9).default(''),
  /** Ordered Destination-master ids rendered as cards in this row. */
  destination_ids: z.array(ItemId).default([]),
});
export type MiceDestinationGroup = z.infer<typeof MiceDestinationGroupSchema>;

export const MiceDestinationsDataSchema = z.object({
  groups: z.array(MiceDestinationGroupSchema).default([]),
});
export type MiceDestinationsData = z.infer<typeof MiceDestinationsDataSchema>;

/**
 * CLIENT_STORY_CARDS - image-rich testimonial carousel. Each card has a hero
 * image, up to three supporting thumbnails, an avatar + traveller name + date,
 * a star rating badge, and a quote (with a read-more toggle when it's long).
 * Section heading + subhead come from the section's title / subtitle columns.
 * (Distinct from the leaner CLIENT_STORIES section.) Media fields reuse the
 * conventional names so the page's media-url collector resolves them for free:
 * image_media_id = hero, gallery_media_ids = thumbnails, photo_media_id = avatar.
 */
export const ClientStoryCardItemSchema = z.object({
  /** Large hero image at the top of the card. */
  image_media_id: ItemId.nullish(),
  /** Up to three supporting thumbnails shown under the hero. */
  gallery_media_ids: z.array(ItemId).max(3).default([]),
  /** Circular traveller avatar next to the name. */
  photo_media_id: ItemId.nullish(),
  /** Traveller name, e.g. "Pardeep Kaur". */
  traveller_name: z.string().max(80).default(''),
  /** Date label shown under the name, e.g. "12 May, 2024". */
  date_label: z.string().max(40).default(''),
  /** Star rating 0-5 shown in the badge (default 5). */
  rating: z.number().min(0).max(5).default(5),
  /** Testimonial quote (clamped with a read-more toggle when long). */
  quote: z.string().max(1000).default(''),
});
export type ClientStoryCardItem = z.infer<typeof ClientStoryCardItemSchema>;

export const ClientStoryCardsDataSchema = z.object({
  items: z.array(ClientStoryCardItemSchema).default([]),
});
export type ClientStoryCardsData = z.infer<typeof ClientStoryCardsDataSchema>;

/**
 * MICE_HERO - "Corporate Travel Made Effortless" hero for the MICE landing page.
 * A full-bleed background image under a purple overlay, with the heading +
 * subhead (from the section title / subtitle columns) and a trust/stats strip on
 * the left, and an inline corporate lead-capture form on the right (the reusable
 * MICE lead form). The four MICE fields fold into the lead message, like
 * MICE_FORM; the lead is recorded under `query_source_code`.
 */
export const MiceHeroDataSchema = z.object({
  /** Full-bleed background image. */
  image_media_id: ItemId.nullish(),
  /** Lead-form heading (the right-hand card). */
  form_title: z.string().max(80).default('Plan Your Corporate Offsite'),
  /** Lead-form submit button label. */
  form_cta_text: z.string().max(40).default('Get Proposal'),
  /** Lead source code recorded against the submission. */
  query_source_code: z.string().min(1).max(40).default('website-mice'),
  /**
   * Trust/stats strip shown under the copy (value + label). Blank rows the
   * editor added but hasn't filled are dropped before validation so an
   * in-progress edit never fails the save.
   */
  stats: z
    .preprocess(
      (v) =>
        Array.isArray(v)
          ? v.filter(
              (s) =>
                s &&
                typeof s === 'object' &&
                (((s as { value?: unknown }).value ?? '') !== '' ||
                  ((s as { label?: unknown }).label ?? '') !== ''),
            )
          : v,
      z.array(HeroStatSchema),
    )
    .default([]),
});
export type MiceHeroData = z.infer<typeof MiceHeroDataSchema>;

/**
 * DESTINATION_SHOWCASE - a "spotlight" slider used for the homepage category
 * rows (International Escapes, Incredible India, Group Tours, …). One layout,
 * themed per instance. The active item shows large (image + name + description
 * + "View all packages"); the upcoming items show as a thumbnail strip you can
 * click, with prev/next arrows. `description` supports simple **bold** spans.
 */
export const DestinationShowcaseItemSchema = z.object({
  name: z.string().max(80).default(''),
  image_media_id: ItemId.nullish(),
  /** Multi-line blurb. Wrap words in **double asterisks** to bold them. */
  description: z.string().max(600).default(''),
  /** "View all packages" link target for this destination. */
  packages_href: z.string().max(300).nullish(),
});
export type DestinationShowcaseItem = z.infer<typeof DestinationShowcaseItemSchema>;

export const DestinationShowcaseDataSchema = z.object({
  // Heading comes from the section's title column.
  /**
   * The category page this showcase is driven by. The API fills `items` at read
   * time from every destination linked to it (image, computed price/count blurb,
   * /<category>/<destination> link), titles it with the category hero heading,
   * and points "View all" at the category page. This is the only mode.
   */
  category_page_id: z.string().uuid().nullish(),
  /** Top-right "View all destinations" pill. */
  view_all_label: z.string().max(60).default('View all destinations'),
  view_all_href: z.string().max(300).nullish(),
  /** Panel gradient base colour - fades diagonally to a near-white tint. */
  panel_colour: ColourHex.default('#BFE7ED'),
  /** Pill / "View all destinations" button background. */
  accent_colour: ColourHex.default('#CDE9F0'),
  /** Auto-filled by the API from the category's destinations (not edited by hand). */
  items: z.array(DestinationShowcaseItemSchema).default([]),
  // ── Slider behaviour (same controls as STAR_ENDORSEMENTS) ──
  autoplay: z.boolean().default(false),
  autoplay_ms: z.number().int().min(1000).max(20000).default(6000),
  pause_on_hover: z.boolean().default(true),
  loop: z.boolean().default(true),
  show_arrows: z.boolean().default(true),
  show_dots: z.boolean().default(false),
  /** How many thumbnails are visible at once, per breakpoint. */
  per_view_desktop: z.number().int().min(1).max(5).default(3),
  per_view_tablet: z.number().int().min(1).max(4).default(2),
  per_view_mobile: z.number().int().min(1).max(2).default(1),
  /** How many cards advance per scroll (manual + autoplay) at each breakpoint. */
  slides_to_scroll_desktop: z.number().int().min(1).max(5).default(1),
  slides_to_scroll_tablet: z.number().int().min(1).max(5).default(1),
  slides_to_scroll_mobile: z.number().int().min(1).max(5).default(1),
});
export type DestinationShowcaseData = z.infer<typeof DestinationShowcaseDataSchema>;

/**
 * GALLERY - "Postcards of Memories" collage. A bulk-uploaded set of traveller
 * photos distributed across vertical columns of staggered "postcards"; on the
 * website adjacent columns drift gently up/down in a permanent, calm loop.
 * Heading / subheading come from the section's own title + subtitle.
 */
export const GalleryDataSchema = z.object({
  /** Collage photos, in display order (round-robin across columns). */
  photo_media_ids: z.array(ItemId).default([]),
  /** Number of columns on desktop (tablet/mobile step down automatically). */
  columns: z.number().int().min(3).max(8).default(7),
  /** Toggle the permanent up/down floating motion. */
  animate: z.boolean().default(true),
});
export type GalleryData = z.infer<typeof GalleryDataSchema>;

/**
 * LOCATIONS - "Our offices" block. Offices are picked from the Branch master
 * (Masters → Branches) by id; the card details (name, badge, address, phone,
 * email) and the Google Maps embed (exact pin) all come from the branch record,
 * so the website stays in sync with the master and the editor only chooses which
 * branches to show. Heading / subhead come from the section title / subtitle.
 */
export const LocationsDataSchema = z.object({
  /** Ordered branch ids (Masters → Branches) to show as office cards. */
  branch_ids: z.array(UuidSchema).default([]),
});
export type LocationsData = z.infer<typeof LocationsDataSchema>;

/**
 * TEXT_BLOCK - a simple reusable "heading + description" content block (e.g.
 * the about page's "Our Story"). The heading comes from the section's title
 * column (and the optional supporting line from subtitle), like every other
 * section; the long-form description lives here in `body` so it can run to
 * multiple paragraphs (split on blank lines at render time). An optional banner
 * image (image_media_id) renders above the text, resolved via the shared media
 * batch like the hero - so it comes from the admin media library.
 */
export const TextBlockDataSchema = z.object({
  /** Multi-paragraph description. Separate paragraphs with a blank line. */
  body: z.string().max(5000).default(''),
  /** Optional small uppercase label shown above the heading. */
  eyebrow: z.string().max(60).nullish(),
  /** Optional banner image shown above the heading (admin media library). */
  image_media_id: ItemId.nullish(),
  /** Text alignment for the heading + body. */
  align: z.enum(['left', 'center']).default('left'),
  /**
   * Visual style:
   *   - `plain` → heading + paragraphs on the page (the "Our Story" look).
   *   - `card`  → the same content inside a filled navy rounded callout box (the
   *               "Don't see the right role?" look). Ignores the banner image.
   */
  variant: z.enum(['plain', 'card']).default('plain'),
  /** Optional CTA button shown under the body (e.g. "View Open Positions"). Both
   *  the label and the link must be set for the button to render. */
  cta_text: z.string().max(60).nullish(),
  cta_href: z.string().max(500).nullish(),
});
export type TextBlockData = z.infer<typeof TextBlockDataSchema>;

/**
 * TEAM_PROFILES - "The minds behind …" people cards (founders / leadership /
 * team). Each card is a photo + name + role + short bio on a soft gradient
 * panel. The section heading / supporting line come from the section's title /
 * subtitle columns; photos come from the admin media library (resolved via the
 * shared media batch, like every other `items[].photo_media_id`).
 */
export const TeamProfileSchema = z.object({
  photo_media_id: ItemId.nullish(),
  name: z.string().max(120).default(''),
  /** Role / title shown under the name, e.g. "Founder & CEO". */
  role: z.string().max(120).default(''),
  /** Short biography paragraph. */
  bio: z.string().max(1200).default(''),
});
export type TeamProfile = z.infer<typeof TeamProfileSchema>;

export const TeamProfilesDataSchema = z.object({
  items: z.array(TeamProfileSchema).default([]),
});
export type TeamProfilesData = z.infer<typeof TeamProfilesDataSchema>;

/**
 * FULL_IMAGE - a reusable "heading + one full-width image" block (e.g. a "Life
 * at Utazzo Holidays" group photo). The heading / supporting line come from the
 * section's title / subtitle columns; the single image comes from the admin
 * media library. `full_bleed` spans the image edge-to-edge; otherwise it sits
 * within the page gutters with rounded corners.
 */
export const FullImageDataSchema = z.object({
  image_media_id: ItemId.nullish(),
  /** Edge-to-edge (true) vs. contained within the page gutters + rounded. */
  full_bleed: z.boolean().default(true),
  /** Image aspect ratio preset. */
  aspect: z.enum(['ultrawide', 'wide', 'standard']).default('wide'),
});
export type FullImageData = z.infer<typeof FullImageDataSchema>;

/**
 * FEATURE_CARDS - a 3-column grid of identical feature cards (e.g. the
 * "Stress-Free Holidays, Always" value props). Each card is an uploaded
 * logo/icon + title + subtitle + a short italic description. The grid heading /
 * supporting line come from the section's title / subtitle columns; the icons
 * come from the admin media library (resolved via the shared media batch like
 * every other `items[].icon_media_id`). Add cards in multiples of 3 (3/6/9…)
 * for full rows.
 */
export const FeatureCardSchema = z.object({
  // `blankable`: the admin repeater starts every field as "", and a card that
  // uses a built-in icon (or none) never replaces it. Without this an untouched
  // image field failed uuid validation and the whole section refused to save.
  icon_media_id: blankable(ItemId.nullish()),
  /**
   * Icon from the shared @utazzo/ui registry (kebab-case name), used by the
   * `trust` variant. An uploaded `icon_media_id` wins when both are set.
   */
  icon: z.string().max(40).nullish(),
  title: z.string().max(80).default(''),
  /** Short tagline under the title. */
  subtitle: z.string().max(160).default(''),
  /** Italic supporting line / quip. */
  description: z.string().max(400).default(''),
  // The two below are used by the destination guides' "explore more" cards,
  // where a card is a photo tile that links somewhere. The icon-tile looks
  // ignore them.
  /** Card photo (media id). */
  image_media_id: blankable(ItemId.nullish()),
  /** Where the card links. Empty = the card is not clickable. */
  href: z.string().max(300).nullish(),
});
export type FeatureCard = z.infer<typeof FeatureCardSchema>;

export const FeatureCardsDataSchema = z.object({
  items: z.array(FeatureCardSchema).default([]),
  /**
   * Visual style:
   *   `tile`     - soft cyan rounded cards (the "Stress-Free Holidays" look).
   *   `plain`    - transparent, centred icon + title + description, no card
   *                chrome (the "Our Culture Commitments" look).
   *   `timeline` - a serpentine timeline: circular navy badges joined by a
   *                snaking line, with the title used as the year/milestone (the
   *                "Growth journey" look).
   *   `steps`    - numbered navy circles (1, 2, 3 … by position) over title +
   *                description, in a row (the "Application Process" look). Icons
   *                are ignored - the number IS the badge.
   */
  /**
   * `trust` (2026-09-19) is the compact reassurance row under a search hero -
   * "Secure payments · 500+ airlines · 24x7 support": an icon tile beside a
   * short title and one line of copy. A variant of this section rather than a
   * section type of its own, so it needed no database enum migration.
   */
  variant: z.enum(['tile', 'plain', 'timeline', 'steps', 'trust']).default('tile'),
  /** Cards per row on desktop. */
  columns: z.number().int().min(2).max(4).default(3),
  /**
   * Small label above the heading ("Keep exploring"). Drawn only by the
   * destination guides' photo-card rail.
   */
  eyebrow: z.string().max(60).nullish(),
});
export type FeatureCardsData = z.infer<typeof FeatureCardsDataSchema>;

/**
 * PROCESS_STEPS - "Our Process" numbered step cards (e.g. the MICE "Brief to
 * Boarding Pass" flow). Each card is a navy "01/02/…" badge (auto-numbered by
 * position) next to a title, over a short description, with a left accent rule.
 * Heading + subhead come from the section's title / subtitle columns; an
 * optional CTA button (e.g. "Get Proposal") renders centred under the grid.
 */
export const ProcessStepSchema = z.object({
  /** Step title, e.g. "Brief & Goals". */
  title: z.string().max(80).default(''),
  /** Short supporting copy for the step. */
  description: z.string().max(400).default(''),
});
export type ProcessStep = z.infer<typeof ProcessStepSchema>;

export const ProcessStepsDataSchema = z.object({
  /** Ordered steps; auto-numbered 01, 02, … by position. */
  items: z.array(ProcessStepSchema).default([]),
  /** Steps per row on desktop. */
  columns: z.number().int().min(2).max(4).default(3),
  /** Optional CTA button under the grid. Hidden when the label is empty. */
  cta_text: z.string().max(40).nullish(),
  cta_href: z.string().max(200).nullish(),
});
export type ProcessStepsData = z.infer<typeof ProcessStepsDataSchema>;

/**
 * VALUE_PROPS - "What's in it for you?" value-prop grid. A centred heading +
 * supporting line over a two-column list of items, each prefixed by a thin
 * colour accent bar (cycled navy → yellow → cyan) with a bold title and a short
 * description. Text-only (no media); heading / subhead come from the section's
 * title / subtitle columns. Renders on a subtle tint gradient background.
 */
export const ValuePropSchema = z.object({
  /** Value-prop title, e.g. "Travel Perks". */
  title: z.string().max(80).default(''),
  /** Short supporting copy for the value prop. */
  description: z.string().max(400).default(''),
});
export type ValueProp = z.infer<typeof ValuePropSchema>;

export const ValuePropsDataSchema = z.object({
  /** Value-prop items; laid out in two columns, accent colours cycled. */
  items: z.array(ValuePropSchema).default([]),
});
export type ValuePropsData = z.infer<typeof ValuePropsDataSchema>;

/**
 * STATS_STRIP - "What Makes Us Different?" figures bar. A single pill-bordered
 * strip of evenly-spaced stat columns (a headline figure stacked over its
 * label, e.g. "15+ Years" / "of Experience"). The heading / supporting line
 * come from the section's title / subtitle columns, like every other section.
 */
export const StatsStripStatSchema = z.object({
  /** The headline figure, e.g. "15+ Years", "100K+". */
  value: z.string().min(1).max(24),
  /** The supporting label beneath it, e.g. "of Experience". */
  label: z.string().min(1).max(60),
  /**
   * lucide icon name. Only the destination guides' card layout draws it (the
   * "Weather Snapshot" tiles); the default figures bar ignores it.
   */
  icon: z.string().max(40).nullish(),
});
export type StatsStripStat = z.infer<typeof StatsStripStatSchema>;

export const StatsStripDataSchema = z.object({
  items: z.array(StatsStripStatSchema).default([]),
});
export type StatsStripData = z.infer<typeof StatsStripDataSchema>;

/**
 * IMAGE_MARQUEE - a single row of uploaded images that slides as a continuous
 * marquee (e.g. a "Life at Utazzo" career-page strip). Upload a handful of
 * photos (5-6 is plenty) and they scroll horizontally on a loop. Heading /
 * supporting line come from the section's title / subtitle columns; the images
 * come from the admin media library (resolved via the shared media batch like
 * GALLERY's `photo_media_ids`). Slider behaviour mirrors the logo strips
 * (autoplay speed, pause-on-hover, per-view counts, stepped vs. continuous).
 */
export const ImageMarqueeDataSchema = z.object({
  /** Marquee images, in display order. */
  photo_media_ids: z.array(ItemId).default([]),
  /** Aspect ratio for each slide. */
  aspect: z.enum(['landscape', 'square', 'portrait', 'wide']).default('landscape'),
  // ── Slider behaviour (same controls as the logo strips) ──
  autoplay: z.boolean().default(true),
  /** Step delay (stepped mode) OR speed driver (continuous mode), ms. */
  autoplay_ms: z.number().int().min(1000).max(20000).default(4000),
  pause_on_hover: z.boolean().default(true),
  loop: z.boolean().default(true),
  /** Visible images per breakpoint. */
  per_view_desktop: z.number().int().min(2).max(8).default(4),
  per_view_tablet: z.number().int().min(2).max(6).default(3),
  per_view_mobile: z.number().int().min(1).max(4).default(2),
  /**
   * Continuous (marquee) mode. When ON the row scrolls smoothly without stepped
   * pauses; autoplay_ms drives the scroll speed (smaller = faster). When OFF it
   * steps every autoplay_ms. Defaults ON for the marquee feel.
   */
  continuous_mode: z.boolean().default(true),
});
export type ImageMarqueeData = z.infer<typeof ImageMarqueeDataSchema>;

/**
 * OPEN_POSITIONS - "Open Positions / Find Your Next Adventure" careers block.
 * A centred heading + a department tab filter (All + each `category`) over a
 * single-open accordion of job cards. The section heading / supporting line
 * come from the section's title / subtitle columns; each opening is a fully
 * authored job (overview + responsibilities + preferred-skills bullets), so the
 * whole list is admin-editable - no media, no references.
 */
export const OpenPositionSchema = z.object({
  /**
   * Stable per-position id. Legacy positions authored before the Position tab
   * have none; the admin backfills a uuid on load so rows are keyable/editable.
   */
  id: z.string().max(64).default(''),
  /**
   * Active flag (the Status toggle in the admin Position tab). Inactive
   * positions are hidden on the public site. Missing = active (legacy items).
   */
  is_active: z.boolean().default(true),
  title: z.string().max(160).default(''),
  /** Department used by the tab filter, e.g. "Sales" | "Marketing". */
  category: z.string().max(80).default(''),
  /**
   * Hiring branch (Masters → Branches). When set, the card's location is the
   * branch's city, resolved at render time. Empty string = none selected.
   */
  branch_id: z.union([UuidSchema, z.literal('')]).default(''),
  /**
   * Legacy free-text location, kept as a fallback for positions authored before
   * `branch_id` and as a manual override when no branch is picked.
   */
  location: z.string().max(120).default(''),
  /** "On-site" | "Remote" | "Hybrid" (free text). */
  work_mode: z.string().max(60).default(''),
  /** Experience band, e.g. "1-3 Years". */
  experience: z.string().max(60).default(''),
  /**
   * Number of open roles for this position, shown as a "N Open Roles" badge on
   * the card. Admin-editable in the Position tab. Defaults to 1 (a position
   * implies at least one opening); set to 0 to hide the badge.
   */
  openings: z.number().int().min(0).max(999).default(1),
  /** Job Overview paragraph. */
  overview: z.string().max(2000).default(''),
  /** Roles & Responsibilities bullets. */
  responsibilities: z.array(z.string().max(500)).default([]),
  /** Preferred Skills & Qualifications bullets. */
  preferred_skills: z.array(z.string().max(500)).default([]),
});
export type OpenPosition = z.infer<typeof OpenPositionSchema>;

export const OpenPositionsDataSchema = z.object({
  items: z.array(OpenPositionSchema).default([]),
});
export type OpenPositionsData = z.infer<typeof OpenPositionsDataSchema>;

/**
 * PACKAGE_GRID - a theme-driven package carousel (e.g. "Most Loved Trips",
 * "Romantic escapes"). The section row's title/subtitle hold the heading +
 * subheading; this payload says which packages to show. Carousels are
 * dynamic-by-tag: the website calls GET /website/packages/search with the
 * tag id (and the page's destination) to resolve the cards live.
 */
export const PackageGridDataSchema = z.object({
  /** Reserved for future "manual" curation; only "theme" is supported today. */
  source: z.literal('theme').default('theme'),
  /** ThemesAndCollections id whose packages fill this carousel. */
  theme_tag_id: ItemId,
  /** Card ordering passed through to the search endpoint. */
  sort_order: z.enum(['newest', 'price_low', 'price_high']).default('newest'),
  /** Max cards to request. */
  limit: z.number().int().min(1).max(24).default(12),
});
export type PackageGridData = z.infer<typeof PackageGridDataSchema>;

/**
 * PACKAGE_CAROUSELS - theme-tagged package carousels (the Destination page's
 * "Sliders" model, as a reusable CMS section for tourism-board / landing pages).
 * Each group is one carousel row: it references a Themes & Collections id
 * (`theme_tag_id`) and the website resolves that collection's packages live via
 * GET /website/packages/search (collections=<id>). `heading` / `subheading` label the
 * row; `sort_order` / `limit` shape the request. Distinct from the single-tag
 * PACKAGE_GRID (left untouched) - this holds MANY rows.
 */
export const PackageCarouselGroupSchema = z.object({
  /** ThemesAndCollections id whose packages fill this row (collection source). */
  theme_tag_id: z.string().max(64).default(''),
  /** Destination master id - when set, shows packages tagged to this destination
   *  (e.g. Europe) instead of a collection. Takes precedence over theme_tag_id. */
  destination_id: z.string().max(64).default(''),
  /** Row heading, e.g. "Discover Europe". */
  heading: z.string().max(120).default(''),
  /** Row subheading. */
  subheading: z.string().max(200).default(''),
  /** Card ordering passed through to the search endpoint. */
  sort_order: z.enum(['newest', 'price_low', 'price_high']).default('newest'),
  /** Max cards to request for this row. */
  limit: z.number().int().min(1).max(24).default(12),
});
export type PackageCarouselGroup = z.infer<typeof PackageCarouselGroupSchema>;

export const PackageCarouselsDataSchema = z.object({
  groups: z.array(PackageCarouselGroupSchema).default([]),
});
export type PackageCarouselsData = z.infer<typeof PackageCarouselsDataSchema>;

/**
 * RICH_TEXT - a free-form SEO/intro prose block (e.g. "About Bali Tour
 * Packages"). Heading comes from the section's title column. `read_more`
 * toggles the website's collapse-after-N-lines behaviour.
 */
export const RichTextDataSchema = z.object({
  // Allow empty so a freshly-added section (created with `{}`) and a draft the
  // editor has been fully cleared in both validate; the author fills it in.
  // No length cap - long legal copy / "Read More" articles can run to any length.
  html: z.string().default(''),
  read_more: z.boolean().default(false),
  /**
   * 2026-09-11. Render the heading + copy inside a white card (rounded, soft
   * shadow - the same card the FAQ block uses) instead of as a plain document.
   * Opt-in for the Reviews page's SEO copy; every existing section stays plain.
   */
  card: z.boolean().default(false),
});
export type RichTextData = z.infer<typeof RichTextDataSchema>;

/**
 * LINK_GROUPS - SEO internal-link clusters ("Bali Packages by Theme",
 * "Bali Packages From City", "Trending Destinations", "Honeymoon Packages").
 * Each group is a heading plus a flat list of {label, href} pills.
 */
export const LinkGroupsDataSchema = z.object({
  groups: z
    .array(
      z.object({
        heading: z.string().max(120).default(''),
        links: z
          .array(
            z.object({
              label: z.string().max(120).default(''),
              href: z.string().max(300).default('/'),
            }),
          )
          .default([]),
      }),
    )
    .default([]),
});
export type LinkGroupsData = z.infer<typeof LinkGroupsDataSchema>;

/**
 * REELS_STRIP - "Creators Travelled With Us" vertical video carousel.
 *
 * Each card is fed one of two ways, and needs exactly one of them:
 *   - `video_media_id`: a video uploaded to the media library (kind=VIDEO),
 *     played in a lightbox.
 *   - `reel_url`: a YouTube Shorts / Instagram Reel link, played inline in the
 *     card. Its cover image is auto-fetched (YouTube serves a static thumbnail;
 *     Instagram is scraped via /api/reel-thumbnail), so `poster_media_id` is
 *     only needed when the editor wants a custom cover.
 *
 * `cta_label` + `cta_href` render the "View Package" pill across the bottom of
 * the card. Heading / subhead come from the section's title / subtitle columns.
 */
/**
 * One vertical video card. Shared by REELS_STRIP and VIDEO_REVIEWS (the
 * reviews page's "Hear It Straight From Our Travellers" grid) so both accept
 * exactly the same inputs: an uploaded video OR a YouTube Shorts / Instagram
 * Reel link, an optional cover, a tag chip and a CTA pill.
 */
export const ReelsStripItemSchema = z
  .object({
    video_media_id: blankable(ItemId.nullish()),
    /** YouTube Shorts or Instagram Reel link - thumbnail auto-fetched. */
    reel_url: blankable(
      z
        .string()
        .trim()
        .refine(isDestinationReelUrl, {
          message:
            'Must be a YouTube Shorts URL (youtube.com/shorts/…) or Instagram Reel URL (instagram.com/reel/…)',
        })
        .nullish(),
    ),
    caption: blankable(z.string().max(120).nullish()),
    poster_media_id: blankable(ItemId.nullish()),
    /** Optional chip overlaid top-left on the card (e.g. a destination). */
    tag: blankable(z.string().max(40).nullish()),
    /** CTA pill across the card's bottom edge - both parts required. */
    cta_label: blankable(z.string().max(40).nullish()),
    cta_href: blankable(z.string().max(500).nullish()),
  })
  .refine((it) => Boolean(it.video_media_id) || Boolean(it.reel_url), {
    message: 'Add an uploaded video or a YouTube Shorts / Instagram Reel link',
    path: ['reel_url'],
  });
export type ReelsStripItem = z.infer<typeof ReelsStripItemSchema>;

export const ReelsStripDataSchema = z.object({
  items: z.array(ReelsStripItemSchema).default([]),
});
export type ReelsStripData = z.infer<typeof ReelsStripDataSchema>;

// ---------------------------------------------------------------------------
// Reviews page sections (2026-09-11)
// ---------------------------------------------------------------------------

/**
 * AWARDS_STATS - "Awarded as India's Fastest Growing Travel Brand". A large
 * award badge image on the left and a white pill-card of small icon tiles on
 * the right (Google 4.4 / 53k Facebook followers / 14+ years …). Each tile is
 * an icon (registry name, e.g. `google` / `facebook` / `instagram` / `award`,
 * or an uploaded icon image) over a bold figure and a small caption.
 *
 * A tile can be fed live from Google: `source: 'google_rating'` swaps the
 * figure for the live Google rating ("4.4") and `'google_review_count'` for
 * the live review count ("500+"). When Google is not configured (no API key /
 * place id in Secrets Manager) the typed `value` is shown instead, so the
 * section never renders blank. Heading comes from the section title column.
 */
export const AwardsStatItemSchema = z.object({
  /** Figure, e.g. "4.4", "53k+", "14+". Also the fallback for Google tiles. */
  value: z.string().max(24).default(''),
  /** Caption under the figure, e.g. "(500+ reviews)", "(Followers)". */
  label: z.string().max(60).default(''),
  /** Icon registry name (lucide or brand key: google / facebook / instagram). */
  icon: blankable(z.string().max(40).nullish()),
  /** Uploaded icon image; wins over `icon` when set. */
  icon_media_id: blankable(ItemId.nullish()),
  /** Live Google feed for this tile (figure only; the caption stays typed). */
  source: z.enum(['manual', 'google_rating', 'google_review_count']).default('manual'),
});
export type AwardsStatItem = z.infer<typeof AwardsStatItemSchema>;

export const AwardsStatsDataSchema = z.object({
  /** The award badge / laurel artwork on the left. */
  badge_media_id: blankable(ItemId.nullish()),
  /** Alt text for the badge, for screen readers. */
  badge_alt: z.string().max(160).default(''),
  items: z.array(AwardsStatItemSchema).default([]),
});
export type AwardsStatsData = z.infer<typeof AwardsStatsDataSchema>;

/**
 * VIDEO_REVIEWS - "Hear It Straight From Our Travellers". A filterable grid
 * of vertical video-review cards. Cards are the same shape as REELS_STRIP
 * (uploaded video or reel link, cover, tag, CTA); the `tag` doubles as the
 * destination filter pill ("Vietnam", "Singapore", …) above the grid. Shows
 * `initial_count` cards, then "Load more" reveals `load_more_step` more.
 */
export const VideoReviewsDataSchema = z.object({
  items: z.array(ReelsStripItemSchema).default([]),
  /** Show the tag filter pills above the grid (needs tags on the cards). */
  show_filters: z.boolean().default(true),
  /** Cards visible before the first "Load more". */
  initial_count: z.number().int().min(1).max(30).default(6),
  /** Cards revealed by each "Load more" click. */
  load_more_step: z.number().int().min(1).max(30).default(6),
  load_more_label: z.string().max(40).default('Load more reviews'),
});
export type VideoReviewsData = z.infer<typeof VideoReviewsDataSchema>;

/**
 * TRAVELLER_REVIEWS - "Thousands of Memories. One Trusted Travel Partner".
 * Stacked review cards: avatar + name + "Reviewed: <date>", a green
 * "★ 5.0/5" rating, a red "Booked: <package>" line, the review text (clamped
 * with Read more) and a strip of trip photos with a "(6+) View All" tile that
 * opens a lightbox.
 *
 * Hybrid feed: the admin-typed `items` are always shown (they carry photos and
 * the booked package, which Google never provides). With
 * `show_google_reviews` on, the live Google reviews (max 5 - the Places API
 * cap; name, rating, text, date, profile photo, no photos) are merged in with
 * a Google badge. Google unconfigured = only the typed reviews render.
 */
export const TravellerReviewItemSchema = z.object({
  /** Circular reviewer avatar. */
  photo_media_id: blankable(ItemId.nullish()),
  reviewer_name: z.string().max(80).default(''),
  /** Date label shown after "Reviewed:", e.g. "07 Jun 2026". */
  date_label: z.string().max(40).default(''),
  /** Star rating 0-5 (default 5). */
  rating: z.number().min(0).max(5).default(5),
  /** Package / trip they booked, shown after "Booked:". */
  booked_label: z.string().max(160).default(''),
  /** Where "Booked:" links (package page). Empty = plain text. */
  booked_href: blankable(z.string().max(300).nullish()),
  /** The review. Clamped with a Read more toggle when long. */
  quote: z.string().max(3000).default(''),
  /** Trip photos, in display order. First 4 show inline; the rest sit behind "View All". */
  gallery_media_ids: z.array(ItemId).default([]),
});
export type TravellerReviewItem = z.infer<typeof TravellerReviewItemSchema>;

export const TravellerReviewsDataSchema = z.object({
  items: z.array(TravellerReviewItemSchema).default([]),
  /**
   * Show the round reviewer photo (avatar) on each card. Off = cards start at
   * the name, no grey placeholder circle; applies to typed and Google reviews.
   */
  show_avatars: z.boolean().default(true),
  /** Merge the live Google reviews into the list. */
  show_google_reviews: z.boolean().default(true),
  /** Where the Google reviews sit relative to the typed ones. */
  google_reviews_position: z.enum(['top', 'bottom']).default('bottom'),
  /** Cards visible before the first "Load more". */
  initial_count: z.number().int().min(1).max(50).default(4),
  /** Cards revealed by each "Load more" click. */
  load_more_step: z.number().int().min(1).max(50).default(4),
  load_more_label: z.string().max(40).default('Load more reviews'),
});
export type TravellerReviewsData = z.infer<typeof TravellerReviewsDataSchema>;

/** One hotel/stay card in the "Handpicked Stays" slider (inline marketing
 * content - price/occupancy are display strings, not live inventory). */
export const HandpickedStaySchema = z.object({
  image_media_id: ItemId.nullish(),
  hotel_name: z.string().max(120).default(''),
  /** First line of the yellow chip, e.g. "1 Night, 2 Adults". */
  occupancy_text: z.string().max(60).default(''),
  /** Second line of the yellow chip, e.g. "₹9,217". */
  price_text: z.string().max(40).default(''),
  href: z.string().max(200).default('/'),
});
export type HandpickedStay = z.infer<typeof HandpickedStaySchema>;

export const HandpickedStaysDataSchema = z.object({
  items: z.array(HandpickedStaySchema).default([]),
  autoplay: z.boolean().default(false),
  autoplay_ms: z.number().int().min(1000).max(20000).default(5000),
  pause_on_hover: z.boolean().default(true),
  loop: z.boolean().default(true),
  show_arrows: z.boolean().default(true),
  show_dots: z.boolean().default(false),
  per_view_desktop: z.number().int().min(1).max(6).default(4),
  per_view_tablet: z.number().int().min(1).max(4).default(2),
  per_view_mobile: z.number().int().min(1).max(2).default(1),
  slides_to_scroll_desktop: z.number().int().min(1).max(5).default(1),
  slides_to_scroll_tablet: z.number().int().min(1).max(5).default(1),
  slides_to_scroll_mobile: z.number().int().min(1).max(5).default(1),
});
export type HandpickedStaysData = z.infer<typeof HandpickedStaysDataSchema>;

/** One theme tile in the "Explore By Themes" slider. */
export const ExploreThemeSchema = z.object({
  image_media_id: ItemId.nullish(),
  label: z.string().max(60).default(''),
  href: z.string().max(200).default('/'),
});
export type ExploreTheme = z.infer<typeof ExploreThemeSchema>;

export const ExploreThemesDataSchema = z.object({
  items: z.array(ExploreThemeSchema).default([]),
  autoplay: z.boolean().default(false),
  autoplay_ms: z.number().int().min(1000).max(20000).default(5000),
  pause_on_hover: z.boolean().default(true),
  loop: z.boolean().default(true),
  show_arrows: z.boolean().default(true),
  show_dots: z.boolean().default(false),
  per_view_desktop: z.number().int().min(1).max(6).default(4),
  per_view_tablet: z.number().int().min(1).max(4).default(2),
  per_view_mobile: z.number().int().min(1).max(2).default(1),
  slides_to_scroll_desktop: z.number().int().min(1).max(5).default(1),
  slides_to_scroll_tablet: z.number().int().min(1).max(5).default(1),
  slides_to_scroll_mobile: z.number().int().min(1).max(5).default(1),
});
export type ExploreThemesData = z.infer<typeof ExploreThemesDataSchema>;

// ---------------------------------------------------------------------------
// Funnel landing pages (/hotels, /flights, /activities) - 2026-09-18
// ---------------------------------------------------------------------------
//
// The three sections below are LIVE: the section row stores only what to show
// (which hotels / cities / routes, how many, for what dates) and the API fills
// in everything a visitor reads - names, photos, star ratings, prices - from
// the TBO mirror and TBO itself. Nothing a supplier can contradict is typed by
// hand: no discount badges, no guest scores, no strike-through prices.

/** How a live row chooses its cards. */
const LiveRowMode = z.enum(['manual', 'trending']);

/**
 * Which day a hotel row is priced for.
 *
 *   - `exact`   today + `lead_days`, literally. What an editor typing "1" means.
 *               The date moves every midnight, so prices are re-fetched daily.
 *   - `friday`  the first Friday on or after that day. A weekend stay is what a
 *               browsing visitor most often has in mind, and the date holds for
 *               a week, so the cached prices do too.
 */
const StayCheckinMode = z.enum(['exact', 'friday']);

/**
 * One hand-picked property. Only `hotel_code` is load-bearing; the two labels
 * are what the editor reads in the admin list, stored so the list does not need
 * a lookup per row to be legible.
 */
export const StayCollectionPickSchema = z.object({
  hotel_code: z.string().trim().max(32).default(''),
  hotel_name: z.string().max(200).default(''),
  city_name: z.string().max(160).default(''),
});
export type StayCollectionPick = z.infer<typeof StayCollectionPickSchema>;

/**
 * STAY_COLLECTION - a row of live hotel cards ("Trending hotels this week",
 * "Luxury collection", "Family-friendly resorts"). One type, used once per row.
 *
 *   - `manual`     the picked properties, in the editor's order.
 *   - `trending`   properties visitors took to checkout most in the last
 *                  `trending_days`, topped up from `items` when traffic is thin.
 *   - `top_rated`  the highest-starred mirrored properties in `city_id`,
 *                  optionally narrowed by an amenity word ("kids", "pool").
 *
 * Prices are quoted for a generated stay - `lead_days` out (see
 * `checkin_mode`), `nights` long, one room for two adults - and the card says so.
 */
export const StayCollectionDataSchema = z.object({
  mode: z.enum(['manual', 'trending', 'top_rated']).default('manual'),
  items: z.array(StayCollectionPickSchema).max(24).default([]),
  /** TBO `CityId`. Required by `top_rated`; narrows `trending` when set. */
  city_id: blankable(z.string().trim().max(40).nullish()),
  city_name: z.string().max(160).default(''),
  min_star: z.number().int().min(3).max(5).default(4),
  amenity_keyword: z.string().trim().max(60).default(''),
  trending_days: z.number().int().min(1).max(90).default(14),
  max_cards: z.number().int().min(3).max(12).default(6),
  lead_days: z.number().int().min(1).max(120).default(14),
  checkin_mode: StayCheckinMode.default('exact'),
  nights: z.number().int().min(1).max(7).default(1),
  view_all_label: z.string().max(40).default(''),
  view_all_href: z.string().max(200).default(''),
});
export type StayCollectionData = z.infer<typeof StayCollectionDataSchema>;

export const StayDestinationPickSchema = z.object({
  /** TBO `CityId`. */
  city_id: z.string().trim().max(40).default(''),
  city_name: z.string().max(160).default(''),
  country_name: z.string().max(120).default(''),
  /** Optional. Without it the tile borrows a photo from the city's own hotels. */
  image_media_id: blankable(ItemId.nullish()),
});
export type StayDestinationPick = z.infer<typeof StayDestinationPickSchema>;

/**
 * STAY_DESTINATIONS - city tiles with a live "1,240 stays" count from the TBO
 * mirror. `tiles` is the photo grid ("Popular destinations"); `compact` is the
 * thumbnail-and-text list ("International destinations").
 */
export const StayDestinationsDataSchema = z.object({
  mode: LiveRowMode.default('manual'),
  layout: z.enum(['tiles', 'compact']).default('tiles'),
  scope: z.enum(['all', 'domestic', 'international']).default('all'),
  items: z.array(StayDestinationPickSchema).max(24).default([]),
  trending_days: z.number().int().min(1).max(90).default(14),
  max_items: z.number().int().min(3).max(16).default(8),
  lead_days: z.number().int().min(1).max(120).default(14),
  checkin_mode: StayCheckinMode.default('exact'),
  nights: z.number().int().min(1).max(7).default(1),
});
export type StayDestinationsData = z.infer<typeof StayDestinationsDataSchema>;

/** Blank while the editor is still filling the row in; otherwise an IATA code. */
const Iata = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^([A-Z]{3})?$/, 'Three-letter IATA code');

export const FlightDealPickSchema = z.object({
  from: Iata.default(''),
  to: Iata.default(''),
  from_label: z.string().max(120).default(''),
  to_label: z.string().max(120).default(''),
  image_media_id: blankable(ItemId.nullish()),
});
export type FlightDealPick = z.infer<typeof FlightDealPickSchema>;

/**
 * FLIGHT_DEALS - route cards priced from TBO's `GetCalendarFare`: the cheapest
 * one-way economy fare in the next `window_days`, and the date it falls on.
 * Indicative by TBO's own description, so the card reads "from" and links to a
 * live search for that date.
 */
export const FlightDealsDataSchema = z.object({
  mode: LiveRowMode.default('manual'),
  items: z.array(FlightDealPickSchema).max(24).default([]),
  trending_days: z.number().int().min(1).max(90).default(14),
  max_cards: z.number().int().min(3).max(12).default(6),
  window_days: z.number().int().min(7).max(60).default(30),
  view_all_label: z.string().max(40).default(''),
  view_all_href: z.string().max(200).default(''),
});
export type FlightDealsData = z.infer<typeof FlightDealsDataSchema>;

export const ActivityPickSchema = z.object({
  image_media_id: blankable(ItemId.nullish()),
  title: z.string().max(160).default(''),
  /** Small line under the title, e.g. "Dubai · 6 hours". */
  meta: z.string().max(120).default(''),
  price_text: z.string().max(40).default(''),
  /** Empty opens the enquiry popup instead of navigating. */
  href: z.string().max(200).default(''),
});
export type ActivityPick = z.infer<typeof ActivityPickSchema>;

export const ActivityPickGroupSchema = z.object({
  heading: z.string().max(120).default(''),
  items: z.array(ActivityPickSchema).max(12).default([]),
});
export type ActivityPickGroup = z.infer<typeof ActivityPickGroupSchema>;

/**
 * ACTIVITY_PICKS - typed activity lists in side-by-side columns ("Handpicked
 * Domestic Activities" / "Handpicked International Activities"). Static: there
 * is no activities supplier behind it yet.
 */
export const ActivityPicksDataSchema = z.object({
  groups: z.array(ActivityPickGroupSchema).max(3).default([]),
  cta_label: z.string().max(30).default('Enquire'),
});
export type ActivityPicksData = z.infer<typeof ActivityPicksDataSchema>;

/**
 * QUICK_INFO - "Quick Information" key/value panel (Discover Europe). A bordered
 * card whose heading comes from the section's title column, over a stacked list
 * of label → value rows (e.g. "Countries Covered" → "40+", "Best Time to Visit"
 * → "April - November", "Ideal Duration" → "7 - 9 Days", "Starting From Per
 * Person" → "₹1,89,900"). Text-only; no media.
 */
export const QuickInfoRowSchema = z.object({
  label: z.string().max(80).default(''),
  value: z.string().max(120).default(''),
});
export type QuickInfoRow = z.infer<typeof QuickInfoRowSchema>;

export const QuickInfoDataSchema = z.object({
  items: z.array(QuickInfoRowSchema).default([]),
});
export type QuickInfoData = z.infer<typeof QuickInfoDataSchema>;

/**
 * BEST_TIME - "Best time to visit" block (Discover Europe). A supporting
 * paragraph (from `body`) over a JAN-DEC month strip whose pills are tinted by
 * their `level` (best / good / fair / low), plus a highlighted "best window"
 * callout (highlight_label + highlight_value, e.g. "Best time to visit" /
 * "April - November") and an optional row of trust stats. Heading / supporting
 * line come from the section's title / subtitle columns; no media.
 */
export const BestTimeMonthSchema = z.object({
  /** Short month label, e.g. "JAN". */
  label: z.string().max(12).default(''),
  /** Highlighted (filled) when this month is part of the best window. */
  active: z.boolean().default(false),
  // The four fields below are drawn only by the destination guides' month-card
  // calendar; the default month strip renders the label alone and ignores them.
  /** lucide icon for the card corner, e.g. sun / cloud / cloud-rain. */
  icon: z.string().max(40).nullish(),
  /** Typical temperature, e.g. "27°C". */
  temp: z.string().max(24).nullish(),
  /** Sunshine level, e.g. "High". */
  sunshine: z.string().max(24).nullish(),
  /** Rainfall level, e.g. "Mid". */
  rainfall: z.string().max(24).nullish(),
});
export type BestTimeMonth = z.infer<typeof BestTimeMonthSchema>;

export const BestTimeDataSchema = z.object({
  /**
   * Small label above the heading ("12-month overview"). Drawn only by the
   * destination guides' month-card calendar.
   */
  eyebrow: z.string().max(60).nullish(),
  /** Supporting paragraph shown above the month strip. */
  body: z.string().max(3000).default(''),
  /** Small label on the highlight callout, e.g. "Best time to visit". */
  highlight_label: z.string().max(60).default('Best time to visit'),
  /** The best window, e.g. "April - November". */
  highlight_value: z.string().max(120).default(''),
  /** JAN-DEC month cells (add all 12 for the full strip). */
  months: z.array(BestTimeMonthSchema).max(12).default([]),
  /** Optional trust stats under the strip (value + label). */
  stats: z
    .array(
      z.object({ value: z.string().max(24).default(''), label: z.string().max(60).default('') }),
    )
    .default([]),
});
export type BestTimeData = z.infer<typeof BestTimeDataSchema>;

/**
 * MONTH_BY_MONTH - the "Month by month" detail cards on the "Best time to
 * visit" guides. One wide card per month: photo on the left, and on the right a
 * temperature chip, the month name, a short summary, pros / cons columns, an
 * activities strip, the month's festivals, a travel tip, and an optional
 * "Read more" paragraph that stays collapsed until the reader asks for it.
 *
 * Distinct from BEST_TIME, which is the compact 12-month calendar row higher up
 * the page - this is the long-form block the calendar links down to. Heading /
 * supporting line come from the section's title / subtitle columns; images
 * resolve via the shared media batch (`image_media_id`).
 */
export const MonthGuideSchema = z.object({
  image_media_id: ItemId.nullish(),
  /** Full month name, e.g. "January". */
  name: z.string().max(40).default(''),
  /** Temperature range for the chip, e.g. "24-31°C". */
  temp: z.string().max(40).default(''),
  /** One or two lines summarising the month. */
  description: z.string().max(600).default(''),
  /** Reasons to come this month. */
  pros: z.array(z.string().max(160)).default([]),
  /** Reasons to think twice. */
  cons: z.array(z.string().max(160)).default([]),
  /** What to do - rendered as a single dot-separated strip. */
  activities: z.array(z.string().max(80)).default([]),
  /** Festivals line, e.g. "Galungan (varies)". */
  festivals: z.string().max(240).default(''),
  /** One practical tip, e.g. what to pack. */
  travel_tip: z.string().max(400).default(''),
  /** Extra prose revealed by "Read more". Empty hides the toggle. */
  details: z.string().max(2000).default(''),
});
export type MonthGuide = z.infer<typeof MonthGuideSchema>;

export const MonthByMonthDataSchema = z.object({
  /** Small label above the heading, e.g. "Month by month". */
  eyebrow: z.string().max(60).default(''),
  /** One card per month, in the order they should read. */
  items: z.array(MonthGuideSchema).max(12).default([]),
});
export type MonthByMonthData = z.infer<typeof MonthByMonthDataSchema>;

/**
 * TRAVEL_STYLES - "Best time based on travel style" on the "Best time to
 * visit" guides. A grid of pale tiles, each an icon, the style's name, a line
 * of copy and a chip naming the months that suit it ("Apr - Jun").
 *
 * Answers the same question as BEST_TIME from the other direction: the
 * calendar says what the weather does, this says which months fit the trip the
 * reader has in mind. Heading / supporting line come from the section's title /
 * subtitle columns; icons resolve through the shared icon registry.
 */
export const TravelStyleSchema = z.object({
  /** lucide icon name for the tile, e.g. heart / mountain / camera. */
  icon: z.string().max(40).default(''),
  /** Style name, e.g. "Honeymoon". */
  title: z.string().max(60).default(''),
  /** One line on what the style gets, e.g. "Quiet beaches, golden sunsets". */
  description: z.string().max(300).default(''),
  /** The chip under the copy - the months that suit it, e.g. "Apr - Jun". */
  when: z.string().max(60).default(''),
});
export type TravelStyle = z.infer<typeof TravelStyleSchema>;

export const TravelStylesDataSchema = z.object({
  /** Small label above the heading, e.g. "Choose your trip". */
  eyebrow: z.string().max(60).default(''),
  items: z.array(TravelStyleSchema).default([]),
});
export type TravelStylesData = z.infer<typeof TravelStylesDataSchema>;

/**
 * RECOMMENDED_PACKAGES - "Recommended <Destination> Tour Packages": a pale
 * panel with an eyebrow, a heading, a "View all" pill and a row of hand-picked
 * package cards (photo, name, starting price, the package's own inclusion
 * lines, and a "View Package" CTA).
 *
 * The cards are VIEWS of the package master, not copies: the editor picks a
 * destination and then the packages to feature, and every card field is read
 * live from Packages, so a price or inclusion edited in the CRM is reflected
 * here without touching the page. Picking nothing falls back to the newest
 * packages for the destination, so the section is never empty. "View all"
 * points at the destination's own page unless an href is given.
 */
export const RecommendedPackagesDataSchema = z.object({
  /** Small label above the heading, e.g. "Hand-picked". */
  eyebrow: z.string().max(60).default(''),
  /**
   * Destination master id. Drives the "View all" link and, when no packages are
   * picked, which packages fill the row.
   */
  destination_id: z.string().max(64).default(''),
  /** The featured packages, in display order. Empty = auto-fill. */
  package_ids: z.array(z.string().max(64)).default([]),
  /** How many cards to auto-fill when `package_ids` is empty. */
  limit: z.number().int().min(1).max(12).default(3),
  /** Label on each card's CTA. */
  cta_label: z.string().max(40).default('View Package'),
  /** Label on the pill top-right. Empty hides the pill. */
  view_all_label: z.string().max(40).default('View all'),
  /** Override for the "View all" target; empty uses the destination's page. */
  view_all_href: z.string().max(200).default(''),
  /** Line above the price, e.g. "Starting from". */
  price_prefix: z.string().max(40).default('Starting from'),
  /** Suffix beside the price, e.g. "/ person". */
  price_suffix: z.string().max(30).default('/ person'),
  /** Max inclusion lines listed per card. */
  bullets_per_card: z.number().int().min(1).max(8).default(4),
});
export type RecommendedPackagesData = z.infer<typeof RecommendedPackagesDataSchema>;

/**
 * PLACES_GRID - "Places to visit" cards. On tourism boards each item is a large
 * image with a vertical place label + description (carousel). On Best Places
 * guides each item is a compact card: image, name, description, a meta row and
 * a Read More CTA that expands the card to the full authored body.
 *
 * The list is flat, but entries flagged `is_heading` split it into named groups
 * ("Best Cities to Visit", "Best Beaches to Visit", …), so one guide can run to
 * 50+ places without reading as a single wall of cards. Heading / supporting
 * line come from the section's title / subtitle columns; images resolve via the
 * shared media batch (`image_media_id`).
 */
/**
 * One cell of a place's meta row — an author-named detail rather than a fixed
 * field, because what's worth stating changes with the kind of place: a city
 * wants "Best time to visit" and "Days needed", a temple wants "Timings" and
 * "Entry fee", a night market wants neither.
 */
export const PlaceMetaSchema = z.object({
  label: z.string().max(40).default(''),
  value: z.string().max(80).default(''),
});
export type PlaceMeta = z.infer<typeof PlaceMetaSchema>;

export const PlaceItemSchema = z.object({
  image_media_id: ItemId.nullish(),
  /**
   * Turns this entry into a group heading instead of a place: the list runs
   * flat, and each heading opens a new group that owns every place after it
   * until the next one ("Best Cities to Visit" … "Best Beaches to Visit").
   * The heading's text is `name`; every other field is ignored.
   *
   * Modelled as a marker inside the same array rather than nested groups so
   * that ordering, moving and deleting work through the one list the admin
   * already has — and so a guide written before headings existed parses and
   * renders exactly as it did, as a single untitled group.
   */
  is_heading: z.boolean().default(false),
  /**
   * Turns this entry into an inline "Related Packages" block instead of a
   * place, so packages can sit BETWEEN sections of the list rather than only
   * once at a fixed position — a long guide sells better with a packages row
   * every few sections than with one buried after the fourth card.
   *
   * The block reuses the page's own RECOMMENDED_PACKAGES section for what it
   * shows; this marker only says where. `name` overrides its heading when set.
   * A list carrying any of these takes full control of placement: the
   * automatic after-the-first-group split stops applying.
   */
  is_packages: z.boolean().default(false),
  /**
   * Place name, e.g. "Uluwatu Temple" — the heading text when `is_heading`, and
   * the optional heading override when `is_packages`.
   */
  name: z.string().max(80).default(''),
  /**
   * Plain-text mirror of `description_html`, kept in sync by the admin form.
   * It predates the rich body and is what pre-editor content still lives in, so
   * renderers fall back to it when `description_html` is empty. It is also the
   * only safe source anywhere the copy has to be text rather than markup.
   */
  description: z.string().max(600).default(''),
  /**
   * The place's body as authored HTML - the same Quill editor the blog uses:
   * headings, bullets, numbered lists, links, bold, highlights. No length cap
   * (a place write-up is an article, not a caption), and no fixed structure -
   * each place carries whatever sections its own information calls for.
   */
  description_html: z.string().default(''),
  /** Optional "read more" / detail link. Empty = no Read More button at all. */
  href: z.string().max(300).nullish(),
  /**
   * The card's meta row, as author-named label/value pairs. Empty falls back to
   * the three fixed fields below, so cards written before this existed keep
   * their exact meta row — that fallback is the only reason those three are
   * still here, and new cards should use this instead.
   */
  meta: z.array(PlaceMetaSchema).default([]),
  /** @deprecated Legacy fixed meta — superseded by `meta`. Best season window. */
  best_time: z.string().max(60).default(''),
  /** @deprecated Legacy fixed meta — superseded by `meta`. Opening hours. */
  timings: z.string().max(60).default(''),
  /** @deprecated Legacy fixed meta — superseded by `meta`. Entry fee. */
  entry_fee: z.string().max(80).default(''),
  /** CTA label on guide cards. */
  cta_label: z.string().max(40).default('Read More'),
});
export type PlaceItem = z.infer<typeof PlaceItemSchema>;

export const PlacesGridDataSchema = z.object({
  /**
   * Uncapped on purpose: a destination guide can legitimately list 50+ places,
   * and this schema also runs on the read path - any cap here would 400 the
   * save AND break rendering of a page that already holds more.
   */
  items: z.array(PlaceItemSchema).default([]),
});
export type PlacesGridData = z.infer<typeof PlacesGridDataSchema>;

/**
 * EVENTS_GRID - "Discover Events Across" cards (Discover Europe). Each event is
 * a hero image + name (e.g. "La Tomatina, Spain") + description + a "Key
 * Capabilities" bullet list. `capabilities_heading` labels the bullet list.
 * Heading / supporting line come from the section's title / subtitle columns;
 * images resolve via the shared media batch (`image_media_id`).
 */
export const EventItemSchema = z.object({
  image_media_id: ItemId.nullish(),
  /** Event name, e.g. "La Tomatina, Spain". */
  name: z.string().max(120).default(''),
  description: z.string().max(600).default(''),
  /**
   * When the event falls, e.g. "March" or "June - July". Shown above the name
   * on the destination guides' festival cards; the default events layout has no
   * place for it and ignores it.
   */
  when: z.string().max(60).nullish(),
  /** Bullet list shown under the "Key Capabilities" heading. */
  capabilities: z.array(z.string().max(160)).default([]),
  /** Optional detail link for the event. Empty = no link. */
  href: z.string().max(300).nullish(),
});
export type EventItem = z.infer<typeof EventItemSchema>;

export const EventsGridDataSchema = z.object({
  items: z.array(EventItemSchema).default([]),
  /** Label for each card's bullet list. */
  capabilities_heading: z.string().max(80).default('Key Capabilities'),
  /**
   * Small label above the heading ("Cultural calendar"). Drawn only by the
   * destination guides' festival-card grid.
   */
  eyebrow: z.string().max(60).nullish(),
});
export type EventsGridData = z.infer<typeof EventsGridDataSchema>;

/**
 * ACTIVITIES_GRID - "Your People. Your Place." travel-style bubbles (Discover
 * Europe). Circular image "bubbles" with a label pill (Family, Couple, Friends,
 * Solo, Wellness, Adventure). Heading / supporting line come from the section's
 * title / subtitle columns; images resolve via the shared media batch
 * (`image_media_id`).
 */
export const ActivityItemSchema = z.object({
  image_media_id: ItemId.nullish(),
  /** Travel-style label, e.g. "Family". */
  label: z.string().max(40).default(''),
  href: z.string().max(200).default('/'),
});
export type ActivityItem = z.infer<typeof ActivityItemSchema>;

export const ActivitiesGridDataSchema = z.object({
  items: z.array(ActivityItemSchema).default([]),
});
export type ActivitiesGridData = z.infer<typeof ActivitiesGridDataSchema>;

/**
 * VISION_MISSION - a two-column "Vision" / "Mission" statement card (About Us).
 * Each column is a short heading + a paragraph. Text-only; an optional section
 * heading can come from the section's title column.
 */
export const VisionMissionDataSchema = z.object({
  vision_title: z.string().max(60).default('Vision'),
  vision_body: z.string().max(600).default(''),
  mission_title: z.string().max(60).default('Mission'),
  mission_body: z.string().max(600).default(''),
});
export type VisionMissionData = z.infer<typeof VisionMissionDataSchema>;

/**
 * CELEBRATION - an anniversary / milestone block (About Us "15 Years. Countless
 * Memories. One Promise."). A bold heading + a description paragraph over a
 * horizontal image carousel. Heading / description come from the section's title
 * / subtitle columns; images resolve via the shared media batch.
 */
export const CelebrationDataSchema = z.object({
  /** Carousel photos, in display order. */
  photo_media_ids: z.array(ItemId).default([]),
});
export type CelebrationData = z.infer<typeof CelebrationDataSchema>;

/**
 * CALLBACK_CHAT - compact "Ready to Plan your Trip?" lead form. Labelled Name /
 * Phone / Email fields over a "Request a callback" submit button (records a lead
 * under `query_source_code`) and a "Chat With Us" WhatsApp button
 * (`whatsapp_href`). Heading / subheading come from the section's title /
 * subtitle columns. Distinct from CALLBACK_FORM (left untouched).
 */
export const CallbackChatDataSchema = z.object({
  /** Lead source code recorded against the submission. */
  query_source_code: z.string().min(1).max(40).default('website-callback'),
  /** Optional default destination for this form (pre-fills the Destination field). */
  destination_id: ItemId.nullish(),
  /** WhatsApp / chat link the "Chat With Us" button opens. Empty = button hidden. */
  whatsapp_href: z.string().max(300).default(''),
  /** Submit button label. */
  callback_label: z.string().max(40).default('Request a callback'),
  /** Chat button label. */
  chat_label: z.string().max(40).default('Chat With Us'),
  /**
   * Mid-page (card) form toggles. `card_show_form` false hides that whole
   * card; the other two hide just the matching button on it.
   */
  card_show_form: z.boolean().default(true),
  card_show_callback: z.boolean().default(true),
  card_show_chat: z.boolean().default(true),
  /**
   * Closing (strip, below the FAQ) form toggles - same meaning as the card
   * flags above, for the second render of this section on landing / tourism
   * board pages.
   */
  strip_show_form: z.boolean().default(true),
  strip_show_callback: z.boolean().default(true),
  strip_show_chat: z.boolean().default(true),
});
export type CallbackChatData = z.infer<typeof CallbackChatDataSchema>;

/**
 * One hand-written card in a LANDING_PACKAGES section. Every field is typed by
 * the editor - nothing is read from the package master, so a campaign card can
 * advertise an offer that has no package row behind it yet.
 */
export const LandingPackageCardSchema = z.object({
  /** Small uppercase line above the name, e.g. "6N/7D". Free text - the card
   *  prints it verbatim rather than composing it from night/day counts. */
  duration_label: z.string().max(40).default(''),
  /** Card heading, e.g. "Romantic Vietnam Odyssey: 7 Days / 6 Nights". */
  name: z.string().max(160).default(''),
  /** Grey line under the name, e.g. "Vietnam". Also seeds the Destination field
   *  of the enquiry popup opened by this card's CTA. */
  location: z.string().max(120).default(''),
  /**
   * Inclusion chips, picked from the Hero Inclusions master (Transfer / Stay /
   * Breakfast / ...). Stored as ids so each chip renders that master's logo,
   * and re-uploading a logo updates every landing page at once.
   */
  inclusion_ids: z.array(ItemId).default([]),
  /** Bullet lines inside the card's "Highlights" accordion. */
  highlights: z.array(z.string().max(200)).default([]),
  /**
   * Price shown after the ₹. Digits-only entries are grouped Indian-style at
   * render ("53999" → "53,999"); anything else prints exactly as typed, so an
   * editor can write "53,999 onwards" when a plain number won't do.
   */
  price: z.string().max(40).default(''),
  /** Optional struck-through price shown before `price`. Empty = hidden. */
  strike_price: z.string().max(40).default(''),
  /** Uploaded / library images for the left carousel, in display order. */
  image_media_ids: z.array(ItemId).default([]),
  /**
   * Pasted image URLs, rendered after `image_media_ids` in the same carousel.
   * Lets a campaign go live with artwork hosted elsewhere, no upload needed.
   */
  image_urls: z.array(z.string().max(500)).default([]),
});
export type LandingPackageCard = z.infer<typeof LandingPackageCardSchema>;

/**
 * LANDING_PACKAGES - hand-written package cards for marketing landing pages
 * (/lp/<slug>). Each `items` entry renders as a horizontal card (image
 * carousel left, details + inclusions + highlights accordion + price/CTA
 * right).
 *
 * The cards are standalone content, NOT views of the package master: campaign
 * pages advertise offers that often differ from - or predate - anything in
 * Packages, so the editor writes every field by hand and a package edit never
 * silently rewrites a live ad landing page.
 */
export const LandingPackagesDataSchema = z.object({
  /**
   * Campaign destination (e.g. Europe). Nothing on the card reads it - it is
   * the page's destination for lead attribution: PageSections hands it to
   * CALLBACK_CHAT so Lead Parsing files the callback under the right place.
   */
  destination_id: ItemId.nullish(),
  /** The cards, in display order. */
  items: z.array(LandingPackageCardSchema).default([]),
  /** CTA label on each card. */
  cta_label: z.string().max(40).default('Customize Package'),
  /**
   * Lead source code recorded against a "Customize Package" submission. The CTA
   * opens the site enquiry popup, but files under this code rather than the
   * popup's own - someone customising a specific package is a different (much
   * warmer) lead than a passing popup enquiry, and the CRM's Lead Parsing
   * System can only separate the two if they carry different query sources.
   */
  query_source_code: z.string().min(1).max(40).default('website-customize-package'),
  /** Small note under the price, e.g. "*Price Varies By Travel Month & Preferences". */
  price_note: z.string().max(120).default('*Price Varies By Travel Month & Preferences'),
  /** Footer line under the CTA, e.g. "Customized by 32 Travelers". Empty = hidden. */
  footnote: z.string().max(80).default(''),
});
export type LandingPackagesData = z.infer<typeof LandingPackagesDataSchema>;

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

/**
 * Dispatches on sectionType. Throws if the dataJson doesn't match the
 * expected shape for that type. Used at API request validation + at
 * website render-time for type narrowing.
 */
export function parseSectionData(sectionType: string, raw: unknown): unknown {
  switch (sectionType) {
    case 'HERO':
      return HeroSectionDataSchema.parse(raw);
    case 'SERVICE_ICONS_ROW':
      return ServiceIconsRowDataSchema.parse(raw);
    case 'STAR_ENDORSEMENTS':
      return StarEndorsementsDataSchema.parse(raw);
    case 'FEATURED_CATEGORY':
      return FeaturedCategoryDataSchema.parse(raw);
    case 'EDITORS_PICKS':
      return EditorsPicksDataSchema.parse(raw);
    case 'EXPLORE_STORIES':
      return ExploreStoriesDataSchema.parse(raw);
    case 'CTA_BANNER':
      return CtaBannerDataSchema.parse(raw);
    case 'FEATURE_LIST':
      return FeatureListDataSchema.parse(raw);
    case 'CLIENT_STORIES':
      return ClientStoriesDataSchema.parse(raw);
    case 'REVIEWS':
      return ReviewsDataSchema.parse(raw);
    case 'AWARDS_BLOCK':
      return AwardsBlockDataSchema.parse(raw);
    case 'CALLBACK_FORM':
      return CallbackFormDataSchema.parse(raw);
    case 'MICE_FORM':
      return MiceFormDataSchema.parse(raw);
    case 'FAQ_LIST':
      return FaqListDataSchema.parse(raw);
    case 'PARTNERS_LOGOS':
      return PartnersLogosDataSchema.parse(raw);
    case 'MICE_ACCREDITATIONS':
      return MiceAccreditationsDataSchema.parse(raw);
    case 'PRESS_LOGOS':
      return PressLogosDataSchema.parse(raw);
    case 'MICE_PARTNERS_MARQUEE':
      return MicePartnersMarqueeDataSchema.parse(raw);
    case 'MICE_SERVICES':
      return MiceServicesDataSchema.parse(raw);
    case 'MICE_HERO':
      return MiceHeroDataSchema.parse(raw);
    case 'MICE_CASE_STUDIES':
      return MiceCaseStudiesDataSchema.parse(raw);
    case 'MICE_DESTINATIONS':
      return MiceDestinationsDataSchema.parse(raw);
    case 'CLIENT_STORY_CARDS':
    case 'MICE_CLIENT_STORIES':
      return ClientStoryCardsDataSchema.parse(raw);
    case 'DESTINATION_SHOWCASE':
      return DestinationShowcaseDataSchema.parse(raw);
    case 'GALLERY':
      return GalleryDataSchema.parse(raw);
    case 'LOCATIONS':
      return LocationsDataSchema.parse(raw);
    case 'TEXT_BLOCK':
      return TextBlockDataSchema.parse(raw);
    case 'TEAM_PROFILES':
      return TeamProfilesDataSchema.parse(raw);
    case 'FULL_IMAGE':
      return FullImageDataSchema.parse(raw);
    case 'FEATURE_CARDS':
      return FeatureCardsDataSchema.parse(raw);
    case 'PROCESS_STEPS':
      return ProcessStepsDataSchema.parse(raw);
    case 'VALUE_PROPS':
      return ValuePropsDataSchema.parse(raw);
    case 'STATS_STRIP':
      return StatsStripDataSchema.parse(raw);
    case 'IMAGE_MARQUEE':
      return ImageMarqueeDataSchema.parse(raw);
    case 'OPEN_POSITIONS':
      return OpenPositionsDataSchema.parse(raw);
    case 'PACKAGE_GRID':
      return PackageGridDataSchema.parse(raw);
    case 'RICH_TEXT':
      return RichTextDataSchema.parse(raw);
    case 'LINK_GROUPS':
      return LinkGroupsDataSchema.parse(raw);
    case 'REELS_STRIP':
      return ReelsStripDataSchema.parse(raw);
    case 'AWARDS_STATS':
      return AwardsStatsDataSchema.parse(raw);
    case 'VIDEO_REVIEWS':
      return VideoReviewsDataSchema.parse(raw);
    case 'TRAVELLER_REVIEWS':
      return TravellerReviewsDataSchema.parse(raw);
    case 'HANDPICKED_STAYS':
      return HandpickedStaysDataSchema.parse(raw);
    case 'EXPLORE_THEMES':
      return ExploreThemesDataSchema.parse(raw);
    case 'STAY_COLLECTION':
      return StayCollectionDataSchema.parse(raw);
    case 'STAY_DESTINATIONS':
      return StayDestinationsDataSchema.parse(raw);
    case 'FLIGHT_DEALS':
      return FlightDealsDataSchema.parse(raw);
    case 'ACTIVITY_PICKS':
      return ActivityPicksDataSchema.parse(raw);
    case 'QUICK_INFO':
      return QuickInfoDataSchema.parse(raw);
    case 'BEST_TIME':
      return BestTimeDataSchema.parse(raw);
    case 'MONTH_BY_MONTH':
      return MonthByMonthDataSchema.parse(raw);
    case 'TRAVEL_STYLES':
      return TravelStylesDataSchema.parse(raw);
    case 'RECOMMENDED_PACKAGES':
      return RecommendedPackagesDataSchema.parse(raw);
    case 'PLACES_GRID':
      return PlacesGridDataSchema.parse(raw);
    case 'EVENTS_GRID':
      return EventsGridDataSchema.parse(raw);
    case 'ACTIVITIES_GRID':
      return ActivitiesGridDataSchema.parse(raw);
    case 'PACKAGE_CAROUSELS':
      return PackageCarouselsDataSchema.parse(raw);
    case 'VISION_MISSION':
      return VisionMissionDataSchema.parse(raw);
    case 'CELEBRATION':
      return CelebrationDataSchema.parse(raw);
    case 'CALLBACK_CHAT':
      return CallbackChatDataSchema.parse(raw);
    case 'LANDING_PACKAGES':
      return LandingPackagesDataSchema.parse(raw);
    default:
      throw new Error(`Unknown section type: ${sectionType}`);
  }
}
